import { Button } from "@/lib/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { Switch } from "@/lib/components/Switch";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { isAutoDuckingEnabled } from "@repo/api/utils/composition";
import { IconAdjustmentsHorizontal, IconRefresh } from "@tabler/icons-react";
import { useCallback } from "react";
import { formatSeconds, SliderRow, useDraftValue } from "../SliderRow";

const MIN_AUDIBLE_WINDOW_MS = 100;
const MAX_FADE_MS = 10_000;
export const MAX_SILENCE_MS = 30_000;

function MusicOptionsDropdown({
  videoDurationMs,
}: {
  videoDurationMs: number;
}) {
  const bm = useEditorStore(
    (state) => state.video?.compositionJson.backgroundMusic,
  );
  const hasVoiceOver = useEditorStore(
    (state) => state.video?.compositionJson.voiceOver !== undefined,
  );
  const autoDucking = useEditorStore(
    (state) =>
      !!state.video && isAutoDuckingEnabled(state.video.compositionJson),
  );
  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const fadeInMs = bm?.fadeInMs ?? 0;
  const fadeOutMs = bm?.fadeOutMs ?? 1000;
  const startSilenceMs = bm?.startSilenceMs ?? 0;
  const endSilenceMs = bm?.endSilenceMs ?? 0;

  const fadeInDraft = useDraftValue(fadeInMs);
  const fadeOutDraft = useDraftValue(fadeOutMs);
  const startSilenceDraft = useDraftValue(startSilenceMs);
  const endSilenceDraft = useDraftValue(endSilenceMs);

  const silenceCeilingMs = Math.min(
    MAX_SILENCE_MS,
    Math.max(0, videoDurationMs - MIN_AUDIBLE_WINDOW_MS),
  );
  const fadeInMax = MAX_FADE_MS;
  const fadeOutMax = MAX_FADE_MS;
  const startSilenceMax = silenceCeilingMs;
  const endSilenceMax = silenceCeilingMs;

  const commit = useCallback(
    (mutator: (draftBm: NonNullable<typeof bm>) => void) => {
      updateVideo((video) => {
        if (!video.compositionJson.backgroundMusic) return;
        mutator(video.compositionJson.backgroundMusic);
      });
    },
    [updateVideo],
  );

  const onCommitFadeIn = (value: number) => {
    const clamped = Math.max(0, Math.min(value, fadeInMax));
    fadeInDraft.commit();
    commit((bm) => {
      bm.fadeInMs = clamped;
    });
  };

  const onCommitFadeOut = (value: number) => {
    const clamped = Math.max(0, Math.min(value, fadeOutMax));
    fadeOutDraft.commit();
    commit((bm) => {
      bm.fadeOutMs = clamped;
    });
  };

  const onCommitStartSilence = (value: number) => {
    const clamped = Math.max(0, Math.min(value, startSilenceMax));
    startSilenceDraft.commit();
    commit((bm) => {
      bm.startSilenceMs = clamped;
    });
  };

  const onCommitEndSilence = (value: number) => {
    const clamped = Math.max(0, Math.min(value, endSilenceMax));
    endSilenceDraft.commit();
    commit((bm) => {
      bm.endSilenceMs = clamped;
    });
  };

  const onReset = () => {
    updateVideo((video) => {
      video.compositionJson.autoDucking = true;

      const bm = video.compositionJson.backgroundMusic;
      if (!bm) return;

      delete bm.startMs;
      delete bm.fadeInMs;
      delete bm.fadeOutMs;
      delete bm.startSilenceMs;
      delete bm.endSilenceMs;
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="iconSm" variant="outline" aria-label="Music options">
          <IconAdjustmentsHorizontal strokeWidth={2} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-80 p-0"
        align="end"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mb-3 flex flex-col gap-4 px-2 pt-4">
          <SliderRow
            label="Fade in"
            info="Gradually raises the music volume from silence over this duration at the start."
            value={fadeInDraft.value}
            onValueChange={fadeInDraft.setDraft}
            onValueCommit={onCommitFadeIn}
            min={0}
            max={fadeInMax}
            step={100}
            formatValue={formatSeconds}
          />

          <SliderRow
            label="Fade out"
            info="Gradually lowers the music volume to silence over this duration at the end."
            value={fadeOutDraft.value}
            onValueChange={fadeOutDraft.setDraft}
            onValueCommit={onCommitFadeOut}
            min={0}
            max={fadeOutMax}
            step={100}
            formatValue={formatSeconds}
          />

          <SliderRow
            label="Silence before music"
            info="Sets how long the video plays silently before the music starts."
            value={startSilenceDraft.value}
            onValueChange={startSilenceDraft.setDraft}
            onValueCommit={onCommitStartSilence}
            min={0}
            max={startSilenceMax}
            step={100}
            formatValue={formatSeconds}
          />

          <SliderRow
            label="Silence after music"
            info="Cuts the music off early before the video ends, leaving silence at the end."
            value={endSilenceDraft.value}
            onValueChange={endSilenceDraft.setDraft}
            onValueCommit={onCommitEndSilence}
            min={0}
            max={endSilenceMax}
            step={100}
            formatValue={formatSeconds}
          />

          {hasVoiceOver && (
            <SwitchRow
              id="music-auto-ducking"
              label="Lower volume during voiceover"
              checked={autoDucking}
              onCheckedChange={(checked) => {
                updateVideo((video) => {
                  video.compositionJson.autoDucking = checked;
                });
              }}
            />
          )}
        </div>

        <div className="p-4">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onReset}
            iconPosition="leading"
            icon={<IconRefresh />}
          >
            <span>Reset</span>
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SwitchRow({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <DropdownMenuItem
      onSelect={(e) => e.preventDefault()}
      className="flex cursor-default items-center justify-between gap-2 focus:bg-transparent"
    >
      <label htmlFor={id} className="text-xs text-text-primary">
        {label}
      </label>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </DropdownMenuItem>
  );
}

export default MusicOptionsDropdown;
